import { LOINC_LAB_DISPLAY_NAMES } from './loincLabDisplayNames';

/**
 * The chart abbreviation for each curated lab — what a clinician writes in a note ("Cr 0.96, K 4.4")
 * and what a collapsed category shows when a whole panel has to fit on one line. Where the curated
 * display is already an abbreviation (TSH, ALT, WBC) it is repeated here so the table stays complete
 * and a test can say so.
 *
 * Abbreviations are the same in Spanish charting, so there is no translated map; the full display
 * names are what get translated.
 */
export const LOINC_LAB_SHORT_NAMES: Record<string, string> = {
  '718-7': 'Hgb',
  '751-8': 'ANC',
  '777-3': 'PLT',
  '787-2': 'MCV',
  '1558-6': 'FBG',
  '1742-6': 'ALT',
  '1751-7': 'Alb',
  '1884-6': 'ApoB',
  '1920-8': 'AST',
  '1963-8': 'HCO3',
  '1968-7': 'D. bili',
  '1975-2': 'T. bili',
  '2085-9': 'HDL',
  '2093-3': 'TC',
  '2132-9': 'B12',
  '2157-6': 'CK',
  '2160-0': 'Cr',
  '2191-5': 'DHEA-S',
  '2243-4': 'E2',
  '2276-4': 'Ferritin',
  '2339-0': 'BG',
  '2345-7': 'Glucose',
  '2498-4': 'Iron',
  '2500-7': 'TIBC',
  '2571-8': 'TG',
  '2731-8': 'PTH',
  '2777-1': 'Phos',
  '2823-3': 'K',
  '2839-9': 'Prog',
  '2842-3': 'PRL',
  '2857-1': 'PSA',
  '2942-1': 'SHBG',
  '2951-2': 'Na',
  '2986-8': 'Testosterone',
  '2991-8': 'Free T',
  '3016-3': 'TSH',
  '3026-2': 'FT4',
  '3053-6': 'T3',
  '3084-1': 'Uric acid',
  '4537-7': 'ESR',
  '4544-3': 'Hct',
  '4548-4': 'A1c',
  '5671-3': 'Pb',
  '5902-2': 'PT',
  '6299-2': 'BUN',
  '6301-6': 'INR',
  '6690-2': 'WBC',
  '6768-6': 'ALP',
  '8099-4': 'TPO Ab',
  '9318-7': 'UACR',
  '10501-5': 'LH',
  // The two Lp(a) codes report in units that are not interconvertible, so the unit is part of the
  // abbreviation — a bare "Lp(a)" would label two different measurements identically on a line that
  // has no other column to tell them apart. `loincLabDisplayNames.ts` splits them the same way.
  '10835-7': 'Lp(a) mg/dL',
  '11572-5': 'RF',
  '13457-7': 'LDL',
  '14635-7': 'Vit D',
  '14979-9': 'aPTT',
  '15067-2': 'FSH',
  '17861-6': 'Ca',
  '19123-9': 'Mg',
  '20150-9': 'FEV1',
  '20447-9': 'HIV RNA',
  '21198-7': 'hCG',
  '24467-3': 'CD4',
  '30522-7': 'hs-CRP',
  '32218-0': 'CCP Ab',
  '33452-4': 'PEF',
  '33762-6': 'NT-proBNP',
  '33914-3': 'eGFR',
  '43583-0': 'Lp(a) nmol/L',
};

/**
 * The abbreviation for a lab code, falling back to its curated display name. `undefined` for a code
 * this system cannot name — the caller has the reading's own `code.text` for that.
 */
export const getLabResultShortName = (code: string): string | undefined =>
  LOINC_LAB_SHORT_NAMES[code] ?? LOINC_LAB_DISPLAY_NAMES[code];
